import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from 'reactstrap'
import useRouter from 'Checkout/Hooks/useRouter'
import useCustomFields from 'Shared/Hooks/useCustomFields'
import { useCart } from 'Checkout/Hooks/useCart'
import { useEffectAfterMount } from 'Shared/Hooks/useEffectAfterMount'
import { useDebouncedCallback } from 'use-debounce'
import storeConnect from 'Checkout/Redux/Connect'
import DetailsViewSchema from './DetailsViewSchema'
import { detailsSetData, detailsSetError } from 'Checkout/Redux/Actions/Details'
import { routesHandler } from 'Checkout/Routes/routesHandler'
import { skeletonCustomFields } from './skeletonCustomFields'
import FormWithValidations from 'Shared/Formik/FormWithValidations'
import GuestInformation from '../GuestInformation/GuestInformation'
import MoreInformation from 'Shared/CustomFields/MoreInformation'
import Products from 'Checkout/Containers/Products/Products'
import SpecialInformation from '../SpecialInformation'
import VehicleInformation from '../VehicleInformation/VehicleInformation'
import initialState from 'Checkout/Redux/InitialState.js'

const CamperForm = ({ address, address2, city, customFields, dayUse, guests, isGroupBookingAvailable, isRequest,
                      rate, rigLength, rigWidth, setDetails, slug, specialRequests, state, toggleValidations,
                      productsPurchased, useGuests, validations, vehicle, zipCode }) => {
  const router = useRouter()
  const dispatch = useDispatch()
  const editingReservationId = useSelector(state => state.cart.editingReservationId)
  const { attachReservation, updateReservation } = useCart()
  const [getCustomFields, { customFieldsData, loadingFieldData }] = useCustomFields(customFields)
  const customFieldsDataToShow = loadingFieldData ? skeletonCustomFields : customFieldsData.data
  const { guests: guestValidations } = validations || {}
  const clearGuests = dayUse ? initialState.details.guests : guests
  const clearVehicle = dayUse ? initialState.details.vehicle : vehicle

  useEffect(() => {
    const customFieldsArgs = { variables: { isPos: false, slug } }
    getCustomFields(customFieldsArgs)
  }, [])

  const initialValues = { address, address2, city, customFields, guests: clearGuests, productsPurchased,
                          specialRequests, state, useGuests, vehicle: clearVehicle, zipCode }

  const completeForm = values => {
    if (editingReservationId) {
      updateReservation(values)
      return goNext()
    }

    const detailsData = { ...values, vehicle: getVehicleData(values.vehicle) }
    setDetails(detailsData)
    attachReservation(detailsData)
    goNext()
  }

  const getVehicleData = formVehicle => {
    if (rigLength && rigWidth) return { ...formVehicle, length: rigLength, width: rigWidth }

    return formVehicle
  }

  const goNext = () => {
    const isCartAvailable = isGroupBookingAvailable && !isRequest
    const route = isCartAvailable ? routesHandler.cart.path : routesHandler.payment.path
    router.push(route)
  }

  const validationSchema = () => DetailsViewSchema({ canAddVehicle: rate?.canAddVehicle || false, isRequest,
                                                     rigLength, rigRequired: rate?.rigRequired, rigWidth,
                                                     validations })

  const saveDataInStoreDebounced = useDebouncedCallback(values => {
    setDetails(values)
  }, 350)

  return (
    <div className="camper-form">
      <FormWithValidations enableReinitialize={false} initialValues={initialValues}
                           onChangeValues={saveDataInStoreDebounced} onSubmit={completeForm} scrollToError
                           validationSchema={validationSchema}>
        {({ isFormikError, setFieldValue, setTouchFormFields, values }) => {
          const submitDisabled = isFormikError && !!editingReservationId

          const submitFormAction = () => {
            if (editingReservationId) {
              if (isFormikError) {
                setTouchFormFields(true)
                return false
              }

              updateReservation(values, { keepStore: true })
              return true
            }
          }

          useEffectAfterMount(() => {
            if (editingReservationId) {
              submitFormAction()
              dispatch(detailsSetError(isFormikError))
            }
          }, [isFormikError])

          useEffect(() => {
            if (customFieldsData.isCompleted)
              setFieldValue('customFields', customFieldsData.data)
          }, [customFieldsData.isCompleted])

          useEffectAfterMount(() => {
            if (submitFormAction()) router.go(routesHandler.cart.path)
          }, [toggleValidations])

          return (
            <>
              {!dayUse && (
                <>
                  <GuestInformation formikValues={values} guestValidations={validations}
                                    setFieldValue={setFieldValue} />
                  <hr />
                </>
              )}

              {(rate?.canAddVehicle || guestValidations?.extraVehicles?.max) && (
                <>
                  <VehicleInformation setFieldValue={setFieldValue} validations={validations} />

                  <hr />
                </>
              )}

              <Products setFieldValue={setFieldValue} showProductsInDetails />

              <MoreInformation customFieldsData={customFieldsDataToShow} loading={loadingFieldData}
                               setFieldValue={setFieldValue} showDivider />

              {!dayUse && (
                <>
                  <SpecialInformation />

                  <hr />
                </>
              )}

              <Button type="submit" className="btn btn-theme-primary btn-lg mb-5" disabled={submitDisabled}>
                {editingReservationId ? 'Update Cart' : 'Add to Cart'}
              </Button>
            </>
            )
          }}
      </FormWithValidations>
    </div>
  )
}

const mapStateToProps = state => ({
  address: state.details.address,
  address2: state.details.address2,
  city: state.details.city,
  customFields: state.details.customFields,
  dayUse: state.search.dayUse,
  guests: state.details.guests,
  isGroupBookingAvailable: state.campground.isGroupBookingAvailable,
  isRequest: state.campground.isRequest,
  productsPurchased: state.details.productsPurchased,
  rate: state.search.rate,
  rigLength: state.search.rigLength,
  rigWidth: state.search.rigWidth,
  slug: state.campground.slug,
  specialRequests: state.details.specialRequests,
  state: state.details.state,
  toggleValidations: state.details.toggleValidations,
  useGuests: state.details.useGuests,
  vehicle: state.details.vehicle,
  zipCode: state.details.zipCode,
})

const mapDispatchToProps = dispatch => ({
  setDetails: data => dispatch(detailsSetData(data)),
})

export default storeConnect({ mapStateToProps, mapDispatchToProps })(CamperForm)
